/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useParams, useSearchParams } from 'react-router-dom';
import { Fragment, memo, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { LiveChatContext } from '../../screens/LiveChatScreen';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Component imports
import { Avatar, Button, Col, Divider, Result, Row, Skeleton, Space, Spin, Typography } from 'antd';
import { ArrowLeftOutlined, GlobalOutlined, RobotOutlined, TranslationOutlined, UserOutlined } from '@ant-design/icons';
import { AgentAcceptIcon } from '../Icons/AgentAcceptIcon';
import LiveChatSession from './LiveChatSession';
import ConversationStatusTag from './ConversationStatusTag';
import InfiniteScrollList from '../UI/InfiniteScrollList';
import LiveChatInput from './LiveChatInput';
import ReactMarkdown from 'react-markdown';
import { SidebarCollapseIcon } from '../Icons/SidebarCollapseIcon';
import MessengerEmptyState from './MessengerEmptyState';

// Misc imports
import { LIVECHAT_MESSAGE_TYPES, LIVECHAT_MODES, LIVECHAT_PARTICIPANT_TYPES, LIVECHAT_SCREEN_TABS, OUTGOING_EVENTS, REQUEST_STATUSES, CONVERSATION_STATUSES } from '../../constants/liveChat';
import { LANGUAGE_OPTIONS, PLATFORMS } from '../../constants/main';
import { acceptRequest, readConversation } from '../../store/liveChat/liveChatThunks';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useConversationStatus } from '../../utils/hooks/useConversationStatus';
import { useFetchConversationMessagesQuery } from '../../store/liveChat/liveChatApi';
import { useFetchConversationNotesQuery } from '../../store/conversationNotes/conversationNotesApi';
import { useFetchLiveChatApplicationSettingsQuery } from '../../store/liveChatSettings/liveChatSettingsApi';
import formatEndUserName from '../../utils/string/formatEndUserName';
import formatTimestamp from '../../utils/misc/formatTimestamp';
import formatLiveChatSystemMessage from '../../utils/misc/formatLiveChatSystemMessage';
import formatTextLinks from '../../utils/string/formatTextLinks';
import useTheme from '../../utils/hooks/useTheme';

// Constant declarations
const containerNotificationOffset = -250;

const ItemHeader = ({ item, sidebarOpen, setSidebarOpen, setInboxOpen, isMobileSize }) => {
  const { borderColorBase } = useTheme();

  return (
    <Row
      wrap={false}
      align="middle"
      gutter={8}
      className="px-4 border-bottom w-100"
      style={{ minHeight: 66 }}
    >
      {
        isMobileSize ? (
          <Col flex="none">
            <Button
              type="text"
              icon={<ArrowLeftOutlined className="fs-lg"/>}
              onClick={() => setInboxOpen(true)}
            />
          </Col>
        ) : null
      }
      <Col flex="none">
        <Avatar.Group className="v-align-middle">
          <Avatar
            size={44}
            className="fs-lg text-color-default fw-300"
            style={{ backgroundColor: borderColorBase }}
          >
            {item ? formatEndUserName(item.platformUserName) : ''}
          </Avatar>

          <Avatar
            src={item?.tenantImageUrl}
            className="mt-auto"
            style={{ borderWidth: 3, backgroundColor: borderColorBase }}
            size={28}
          />
        </Avatar.Group>
      </Col>

      <Col flex="auto">
        {
          item ? (
            <Fragment>
              <Typography.Paragraph
                className="mb-0 fw-500 fs-lg"
                ellipsis
              >
                {
                  item.platformUserName
                }
                <span className="ml-2">
                  {Object.values(PLATFORMS).find(platform => platform.key === item.platformType)?.icon ?? <GlobalOutlined />}
                </span>
              </Typography.Paragraph>

              <Typography.Paragraph
                className="mb-0"
                ellipsis
              >
                {item.tenantName}
              </Typography.Paragraph>
            </Fragment>
          ) : (
            <Skeleton
              paragraph={{ rows: 1, width: 120, style: { margin: 0, padding: 0 } }}
              style={{ maxWidth: 400 }}
            />
          )
        }
      </Col>

      <Col flex="none">
        <Button
          style={{
            pointerEvents: sidebarOpen ? 'none' : 'auto',
            display: sidebarOpen ? 'none' : 'block',
            transform: 'rotate(180deg)'
          }}
          type="text"
          icon={<SidebarCollapseIcon className="fs-xl" />}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        />
      </Col>
    </Row>
  );
};

const Conversation = () => {
  const { orgId } = useParams();
  const dispatch = useDispatch();
  const messageContainerRef = useRef();
  const { t } = useTranslation();

  const { sidebarOpen, setSidebarOpen, setInboxOpen, isMobileSize } = useContext(LiveChatContext);
  const [hasJoined, isInactive, canRejoin] = useConversationStatus();
  // Store state
  const { currentUser } = useSelector((state) => state.auth);
  const { currentData: conversations } = getCurrentQueryState('fetchConversations', { orgId }, false);

  // Local state
  const [originalMessages, setOriginalMessages] = useState([]);
  const [skip, setSkip] = useState(0);
  const [searchParams] = useSearchParams();
  const [shouldLoadMoreMessages, setShouldLoadMoreMessages] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const { currentData: activeConversationInfo, error: fetchActiveConversationError } = getCurrentQueryState('fetchActiveConversation', { orgId, conversationId: searchParams.get('conversation') });
  const { data: organization } = getCurrentQueryState('fetchOrganization', { orgId });
  const { data: livechatSettings } = useFetchLiveChatApplicationSettingsQuery({ orgId });

  const orgTimezone = organization?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  // eslint-disable-next-line no-unused-vars
  const { currentData, isFetching: isFetchingNotes, isSuccess } = useFetchConversationNotesQuery(
    { conversationId: activeConversationInfo?.id },
    { skip: !activeConversationInfo?.id }
  );

  const { currentData: conversationMessages, isFetching: isFetchingConversationMessages } = useFetchConversationMessagesQuery({
    orgId,
    urlParams: {
      conversation: activeConversationInfo?.id,
      skip: skip,
      take: 50
    },
    replace: skip === 0
  }, { skip: !activeConversationInfo?.id });

  // The following condition checks whether there are more messages left to fetch
  useEffect(() => {
    if (conversationMessages) {
      setShouldLoadMoreMessages(!isFetchingConversationMessages && conversationMessages.data.length < conversationMessages.total);
    }

    // If real time translations are enabled and the message is coming from the agent,
    // show the message in the language of the agent
    if (conversationMessages && livechatSettings?.isTranslationEnabled) {
      const newMessageIds = conversationMessages.data
        .filter(message => message?.from?.participantType === LIVECHAT_PARTICIPANT_TYPES.AGENT && !!message?.originalText)
        .map(message => message.id);

      setOriginalMessages(prevOriginalMessages => {
        const allMessageIds = [...new Set([...prevOriginalMessages, ...newMessageIds])];
        return allMessageIds;
      });
    }

  }, [isFetchingConversationMessages, conversationMessages?.data.length, livechatSettings]);

  useEffect(() => {
    if (activeConversationInfo) {
      const me = activeConversationInfo.livechatParticipants.find(agent => agent.platformUserId === currentUser.id);
      if (me && !me.lastMessageRead) {
        dispatch(readConversation(activeConversationInfo.id));
      }
    }

    return () => {
      setSkip(0);
    };
  }, [activeConversationInfo?.id]);

  useEffect(() => {
    if (messageContainerRef.current?.scrollTop >= containerNotificationOffset) {
      return;
    }

    if (conversationMessages && conversationMessages.data.length && messageContainerRef.current) {

      const { current: container } = messageContainerRef;
      const latestMessage = conversationMessages.data[0];
      if (
        latestMessage.type !== LIVECHAT_MESSAGE_TYPES.SYSTEM_MESSAGE
        && latestMessage.from?.platformUserId === currentUser.id
      ) {
        container.scrollTop = 0;
      } else {
        setHasNewMessages(true);
      }
    }
  }, [conversationMessages?.total]);

  const getNextPage = (skip) => {
    if (skip && searchParams.get('conversation')) {
      setSkip(skip);
    }
  };

  const renderConversation = () => {
    if (conversations && !conversations.data.length && !activeConversationInfo) {
      setSidebarOpen(false);
      return <MessengerEmptyState/>;
    }

    if (fetchActiveConversationError?.error.status && !activeConversationInfo) {
      return (
        <Row
          className="bg-body h-100"
          align="middle"
          justify="center"
        >
          <Result
            status="404"
            title="Oops"
            subTitle={t('The conversation you are trying to access does not exist.')}
          />
        </Row>
      );
    }

    return (
      <Fragment>
        <ItemHeader
          item={activeConversationInfo}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          setInboxOpen={setInboxOpen}
          isMobileSize={isMobileSize}
        />

        <Row
          wrap={false}
          className="flex-column flex-auto bg-layout overflow-y-hidden position-relative"
        >
          <LiveChatSession />

          {/* TODO: This should also be replaced with the new ChatSession component */}
          <Row
            wrap={false}
            className="flex-column-reverse flex-auto overflow-y-auto border-bottom"
            ref={messageContainerRef}
            onScroll={({ target }) => {
              if (target.scrollTop > containerNotificationOffset) {
                setHasNewMessages(false);
              }
            }}
          >
            {
              conversationMessages ? (
                <InfiniteScrollList
                  className="message-list px-3 d-flex flex-column-reverse"
                  dataSource={conversationMessages.data}
                  total={conversationMessages.total}
                  loadMore={shouldLoadMoreMessages}
                  onLoadMore={getNextPage}
                  locale={{ emptyText: t('This is the start of the conversation') }}
                  parentElement={messageContainerRef.current}
                  rowKey="id"
                  renderItem={(message, index) => {
                    if (message.options?.break) {
                      const isTransfer = message.text.includes('transferred');
                      return (
                        <li
                          key={message.id}
                          data-key={message.id}
                          className="mt-10 max-w-100"
                        >
                          <Divider className="system-message-divider">
                            <ConversationStatusTag
                              title={message.text}
                              type={isTransfer ? 'transferred' : 'joined'}
                            />
                          </Divider>
                        </li>
                      );
                    }

                    let hasNext = false;
                    const nextMessage = index === 0 ? null : conversationMessages.data[index-1];
                    const isSameType = nextMessage?.type === message.type;

                    if (nextMessage && !nextMessage.options.break && isSameType && message.from?.id === nextMessage.from?.id && moment(nextMessage.sentDate).diff(message.sentDate, 'seconds') < 300) {
                      hasNext = true;
                    }

                    if (index < conversationMessages.data.length - 1) {
                      const previousMessage = conversationMessages.data[index+1];
                      const currentMessageType = message.type;
                      if (previousMessage?.type === currentMessageType && !previousMessage?.options.break) {
                        const isSystemMessage = currentMessageType === LIVECHAT_MESSAGE_TYPES.SYSTEM_MESSAGE;
                        if ((isSystemMessage || message.from.id === previousMessage.from.id) && moment(message.sentDate).diff(previousMessage.sentDate, 'seconds') < 300) {
                          return <li
                            key={message.id}
                            data-key={message.id}
                          >
                            <Row
                              wrap={false}
                              align="middle"
                              gutter={8}
                              className={classNames({
                                'mid': hasNext,
                                'last': !hasNext,
                                'livechat-message-outgoing': message.from?.participantType === LIVECHAT_PARTICIPANT_TYPES.AGENT && message.type !== LIVECHAT_MESSAGE_TYPES.SYSTEM_MESSAGE,
                                'livechat-message-incoming': !(message.from?.participantType === LIVECHAT_PARTICIPANT_TYPES.AGENT && message.type !== LIVECHAT_MESSAGE_TYPES.SYSTEM_MESSAGE)
                              })}
                            >
                              <Col
                                flex="0 0 44px"
                                className="hidden-timestamp"
                              >
                                <Typography.Text
                                  className="text-muted fs-xs d-none"
                                >
                                  {formatTimestamp(message.sentDate, true, orgTimezone)}
                                </Typography.Text>
                              </Col>

                              <Col
                                flex="auto"
                              >
                                <Row
                                  className="message-bubble"
                                >
                                  <Typography.Text
                                    onMouseOver={({ target }) => {
                                      const wrapperElement = target.closest('li');
                                      if (wrapperElement) {
                                        wrapperElement.querySelector('.hidden-timestamp span').classList.replace('d-none', 'd-inline');
                                      }
                                    }}
                                    onMouseLeave={({ target }) => {
                                      const wrapperElement = target.closest('li');
                                      if (wrapperElement) {
                                        wrapperElement.querySelector('.hidden-timestamp span').classList.replace('d-inline', 'd-none');
                                      }
                                    }}
                                  >

                                    {
                                      message.type === LIVECHAT_MESSAGE_TYPES.TEXT ? (
                                        <ReactMarkdown
                                          skipHtml
                                          allowedElements={['p', 'strong', 'em', 'ul', 'ol', 'li', 'img', 'a']}
                                          unwrapDisallowed
                                          linkTarget={'_blank'}
                                        >
                                          {originalMessages.includes(message.id) ? formatTextLinks(message.originalText) : formatTextLinks(message.text)}
                                        </ReactMarkdown>
                                      ) : formatLiveChatSystemMessage(message.text)
                                    }

                                    {
                                      livechatSettings?.isTranslationEnabled && message.type === LIVECHAT_MESSAGE_TYPES.TEXT && message.originalText && !originalMessages.includes(message.id) ? (
                                        <Button
                                          type="link"
                                          className="fw-400 px-0 fs-sm white-space-normal h-auto text-left"
                                          size="small"
                                          ghost
                                          onClick={() => {
                                            setOriginalMessages([...new Set([message.id, ...originalMessages])]);
                                          }}
                                        >
                                          <Space direction="vertical" size={0} align="start" className="px-1">
                                            <span><TranslationOutlined className="mr-1"/><Typography.Text type="secondary">{t('Auto-translated message from ')} { LANGUAGE_OPTIONS.find(({ key }) => key === message.language)?.label }</Typography.Text></span>
                                            <Fragment>{ t('View original message') }</Fragment>
                                          </Space>
                                        </Button>
                                      ) : null
                                    }

                                    {
                                      livechatSettings?.isTranslationEnabled && message.type === LIVECHAT_MESSAGE_TYPES.TEXT && message.originalText && originalMessages.includes(message.id) ? (
                                        <Button
                                          type="link"
                                          className="fw-400 px-0 fs-sm white-space-normal h-auto text-left"
                                          size="small"
                                          ghost
                                          onClick={() => {
                                            setOriginalMessages(originalMessages.filter((id) => id !== message.id));
                                          }}
                                        >
                                          <Space direction="vertical" size={0} align="start" className="px-1">
                                            <span>
                                              <TranslationOutlined className="mr-1"/>
                                              <Typography.Text type="secondary">
                                                {t('Auto-translated message to ')}
                                                { LANGUAGE_OPTIONS.find(({ key }) => {
                                                  if(message.from.participantType === LIVECHAT_PARTICIPANT_TYPES.AGENT) {
                                                    return key === activeConversationInfo?.platformUserLanguage;
                                                  } else {
                                                    return key === livechatSettings?.agentDefaultLanguage;
                                                  }
                                                } )?.label }
                                              </Typography.Text>
                                            </span>
                                            <Fragment>{ t('View translated message') }</Fragment>
                                          </Space>
                                        </Button>
                                      ) : null
                                    }
                                  </Typography.Text>
                                </Row>
                              </Col>
                            </Row>
                          </li>;
                        }
                      }
                    }

                    return (
                      <li
                        key={message.id}
                        data-key={message.id}
                        className="mt-10"
                      >
                        <Row
                          wrap={false}
                          align="top"
                          gutter={8}
                          className={classNames(
                            {
                              'first': hasNext,
                              'livechat-message-outgoing': message.from?.participantType === LIVECHAT_PARTICIPANT_TYPES.AGENT && message.type !== LIVECHAT_MESSAGE_TYPES.SYSTEM_MESSAGE,
                              'livechat-message-incoming': !(message.from?.participantType === LIVECHAT_PARTICIPANT_TYPES.AGENT && message.type !== LIVECHAT_MESSAGE_TYPES.SYSTEM_MESSAGE)
                            }
                          )}
                        >
                          <Col
                            flex="0 0 44px"
                          >
                            <Avatar
                              size={36}
                              src={
                                message.from?.participantType === LIVECHAT_PARTICIPANT_TYPES.AGENT && message.type !== LIVECHAT_MESSAGE_TYPES.SYSTEM_MESSAGE
                                  ? (message.from?.image ? message.from.image : <AgentAcceptIcon/>)
                                  : (message.type === LIVECHAT_MESSAGE_TYPES.SYSTEM_MESSAGE ? <RobotOutlined /> : <UserOutlined />)
                              }
                              style={{ verticalAlign: -40 }}
                            />
                          </Col>

                          <Col
                            flex="auto"
                          >
                            <Row
                              wrap={false}
                              align="middle"
                              className="fs-sm fw-500 mb-1"
                            >
                              {
                                message.type === LIVECHAT_MESSAGE_TYPES.TEXT ? message.from?.fullName
                                  : message.options?.title ? message.options.title : t('System Message')
                              }
                              <span className="text-muted mx-2">&#183;</span>
                              <span className="text-muted fw-normal fs-xs">
                                {formatTimestamp(message.sentDate, true, orgTimezone)}
                              </span>
                            </Row>
                            <Row
                              className="message-bubble"
                            >
                              <Typography.Text>
                                {
                                  message.type === LIVECHAT_MESSAGE_TYPES.TEXT ? (
                                    <ReactMarkdown
                                      skipHtml
                                      allowedElements={['p', 'strong', 'em', 'ul', 'ol', 'li', 'img', 'a']}
                                      unwrapDisallowed
                                      linkTarget={'_blank'}
                                    >
                                      {originalMessages.includes(message.id) ? formatTextLinks(message.originalText) : formatTextLinks(message.text)}
                                    </ReactMarkdown>
                                  ) : formatLiveChatSystemMessage(message.text)
                                }

                                {
                                  livechatSettings?.isTranslationEnabled && message.type === LIVECHAT_MESSAGE_TYPES.TEXT && message.originalText && !originalMessages.includes(message.id) ? (
                                    <Button
                                      type="link"
                                      size="small"
                                      className="fw-400 px-0 fs-sm white-space-normal h-auto text-left"
                                      onClick={() => {
                                        setOriginalMessages([...new Set([message.id, ...originalMessages])]);
                                      }}
                                      ghost
                                    >
                                      <Space direction="vertical" size={0} align="start" className="px-1">
                                        <span><TranslationOutlined className="mr-1"/><Typography.Text type="secondary">{t('Auto-translated message from ')} { LANGUAGE_OPTIONS.find(({ key }) => key === message.language)?.label }</Typography.Text></span>
                                        <Fragment>{ t('View original message') }</Fragment>
                                      </Space>
                                    </Button>
                                  ) : null
                                }

                                {
                                  livechatSettings?.isTranslationEnabled && message.type === LIVECHAT_MESSAGE_TYPES.TEXT && message.originalText && originalMessages.includes(message.id) ? (
                                    <Button
                                      type="link"
                                      size="small"
                                      className="fw-400 px-0 fs-sm white-space-normal h-auto text-left"
                                      onClick={() => {
                                        setOriginalMessages(originalMessages.filter((id) => id !== message.id));
                                      }}
                                      ghost
                                    >
                                      <Space direction="vertical" size={0} align="start" className="px-1">
                                        <span>
                                          <TranslationOutlined className="mr-1"/>
                                          <Typography.Text type="secondary">{t('Auto-translated message to ')}
                                            { LANGUAGE_OPTIONS.find(({ key }) => {
                                              if(message.from.participantType === LIVECHAT_PARTICIPANT_TYPES.AGENT) {
                                                return key === activeConversationInfo?.platformUserLanguage;
                                              } else {
                                                return key === livechatSettings?.agentDefaultLanguage;
                                              }
                                            } )?.label }
                                          </Typography.Text>
                                        </span>
                                        <Fragment>{ t('View translated message') }</Fragment>
                                      </Space>
                                    </Button>
                                  ) : null
                                }
                              </Typography.Text>
                            </Row>
                          </Col>
                        </Row>
                      </li>
                    );
                  }}
                />
              ) : (
                <div
                  id="messengerLoaderSkeleton"
                  className="messenger-loader-skeleton"
                >
                  {
                    [...Array(6)].map((_, i) => (
                      <Space
                        key={i}
                        wrap={false}
                        size={16}
                        align="start"
                        className="mb-10"
                      >
                        <Skeleton.Avatar size="large" active />
                        <Skeleton.Button size="large" active />
                      </Space>
                    ))
                  }
                </div>
              )
            }
          </Row>

          {
            hasNewMessages ? (
              <Button
                type="primary"
                className="position-absolute mb-4 ml-4 bottom-0"
                style={{ opacity: 0.85 }}
                onClick={() => {
                  if (messageContainerRef.current) {
                    messageContainerRef.current.scrollTop = 0;
                    setHasNewMessages(false);
                  }
                }}
              >
                {t('New messages')}
              </Button>
            ) : null
          }
        </Row>
      </Fragment>
    );
  };

  return (
    <Row
      wrap={false}
      className="h-100 flex-column"
    >
      {renderConversation()}
      {
        conversations?.total > 0 || activeConversationInfo?.id ? (
          <LiveChatInput
            hasJoined={hasJoined}
            isInactive={isInactive}
            canRejoin={canRejoin}
          />
        ) : null
      }
    </Row>
  );
};

const Request = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { orgId } = useParams();

  const { sidebarOpen, setSidebarOpen, selectedRequest, setSelectedRequest, setInboxOpen, isMobileSize } = useContext(LiveChatContext);
  const { data: livechatSettings } = getCurrentQueryState('fetchLiveChatApplicationSettings', { orgId });
  const { currentData: incomingRequests, isFetching: isLoadingRequests } = getCurrentQueryState('fetchRequests', { orgId });

  const [searchParams, setSearchParams] = useSearchParams();
  const [isAcceptingRequest, setIsAcceptingRequest] = useState();
  const [timer, setTimer] = useState(0);

  useEffect(() => {
    const resetTimerOnTabFocus = () => {
      setTimeout(() => {
        if (selectedRequest && selectedRequest.status === REQUEST_STATUSES.NEW) {
          setTimer(moment().diff(selectedRequest.requestedDate, 'seconds'));
        }
      });
    };

    window.addEventListener('focus', resetTimerOnTabFocus);
    return () => window.removeEventListener('focus', resetTimerOnTabFocus);
  }, [selectedRequest?.id]);

  useEffect(() => {
    if (selectedRequest && selectedRequest.status === REQUEST_STATUSES.NEW) {
      const diff = moment().diff(selectedRequest.requestedDate, 'seconds');
      // Add 1 to trigger state update when diff is 0, because timer is initialized as 0
      setTimer(diff === 0 ? 1 : diff);
    }
  }, [selectedRequest?.id]);

  useEffect(() => {
    if (incomingRequests && incomingRequests.data && selectedRequest) {
      const activeRequest = incomingRequests.data.find(request => request.id === selectedRequest.id);
      setSelectedRequest(activeRequest);
      setIsAcceptingRequest(false);
    }
  }, [incomingRequests?.data?.find(request => request.id === selectedRequest?.id)]);

  useEffect(() => {
    let timerInterval;
    if (selectedRequest?.status === REQUEST_STATUSES.NEW) {
      timerInterval = setTimeout(() => {
        setTimer(timer+1);
      }, 1000);
    }

    return () => clearTimeout(timerInterval);
  }, [timer]);

  const onAccept = () => {
    setIsAcceptingRequest(true);
    try {
      const dataToEmit = {
        type: OUTGOING_EVENTS.REQUEST_ACCEPT,
        data: {
          id: selectedRequest?.id
        }
      };
      dispatch(acceptRequest(dataToEmit));
    } catch (error) {
      console.error(error);
    }
  };

  const renderContent = () => {
    if (selectedRequest) {
      const { status } = selectedRequest;

      switch (status) {
        case REQUEST_STATUSES.MISSED:
          return (
            <Typography.Paragraph className="mb-5">{t('Request was missed.')}</Typography.Paragraph>
          );

        case REQUEST_STATUSES.ACCEPTED:
          return (
            <Fragment>
              <Typography.Paragraph className="mb-5">{t('Accepted by')} {selectedRequest?.acceptedBy}</Typography.Paragraph>
              {
                livechatSettings?.livechatMode === LIVECHAT_MODES.PUBLIC ? (
                  <Button
                    size="large"
                    type="primary"
                    block
                    className="mb-10"
                    disabled={!selectedRequest?.livechatConversation}
                    style={{ maxWidth: 280 }}
                    onClick={() => {
                      if (!searchParams) {
                        return;
                      }
                      setSearchParams({
                        tab: selectedRequest?.livechatConversation?.status === CONVERSATION_STATUSES.FINISHED ? LIVECHAT_SCREEN_TABS.CLOSED : LIVECHAT_SCREEN_TABS.OPEN,
                        conversation: selectedRequest?.livechatConversation.id
                      });
                    }}
                  >
                    {t('View Conversation')}
                  </Button>
                ) : null
              }
            </Fragment>
          );

        case REQUEST_STATUSES.NEW:
          return (
            <Fragment>
              <Typography.Paragraph className="mb-0 fs-xl">
                {
                  moment.duration(timer, 'seconds').format('mm:ss', { trim: false })
                }
              </Typography.Paragraph>

              <Typography.Paragraph className="mb-5">{t('waiting for response from me!')}</Typography.Paragraph>

              <Button
                size="large"
                type="primary"
                loading={isAcceptingRequest}
                block
                className="mb-10"
                style={{ maxWidth: 280 }}
                onClick={onAccept}
              >
                {t('Accept Request')}
              </Button>
            </Fragment>
          );

        default:
          break;
      }
    }

    // Else
    return (
      <Row
        align="middle"
        justify="center"
        className="h-100 bg-layout"
      >
        {t('Select a request to view more details.')}
      </Row>
    );
  };

  if (isLoadingRequests) {
    return (
      <Row
        align="middle"
        justify="center"
        className="h-100 bg-layout"
      >
        <Spin />
      </Row>
    );
  }

  if (incomingRequests && !incomingRequests.data?.length) {
    return <MessengerEmptyState/>;
  }

  if (incomingRequests && !selectedRequest) {
    return (
      <Row
        align="middle"
        justify="center"
        className="h-100 bg-layout"
      >
        {t('Select a request to view more details.')}
      </Row>
    );
  }

  // Else
  return (
    <Row
      wrap={false}
      className="h-100 flex-column"
    >
      <ItemHeader
        item={selectedRequest}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        setInboxOpen={setInboxOpen}
        isMobileSize={isMobileSize}
      />

      <Row
        wrap={false}
        align="middle"
        justify="center"
        className="w-100 flex-column flex-auto bg-layout position-relative"
      >
        <LiveChatSession />

        {renderContent()}
      </Row>
    </Row>
  );
};

const LiveChatMessenger = () => {
  const [searchParams] = useSearchParams();
  const isRequestsTab = searchParams.get('tab') === LIVECHAT_SCREEN_TABS.NEW || searchParams.get('tab') === LIVECHAT_SCREEN_TABS.REQUESTS_HISTORY;

  if (isRequestsTab) {
    return <Request />;
  }

  return <Conversation />;
};

ItemHeader.propTypes = {
  item: PropTypes.object,
  sidebarOpen: PropTypes.bool,
  setSidebarOpen: PropTypes.func,
  setInboxOpen: PropTypes.func,
  isMobileSize: PropTypes.bool
};

export default memo(LiveChatMessenger);
